import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import type { GenericField } from '@Types';
import { getShelterNow } from '@Constants';
import { getDefaultDateFormat, getParsedDate } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useForm } from '@FormModule';
import useShoppingCartStore from '../../../../store';

const fieldsSchema = ref<FieldSchema[]>([]);

export default function () {
  const { t } = useI18n();
  const { shelterProfile, userProfile } = storeToRefs(useSessionStore());
  const { getNewestCartAnimalIntakeDate } = useShoppingCartStore();
  const { cartData } = storeToRefs(useShoppingCartStore());

  const { getDetailsSchema } = useForm();

  const currentSchemaModels = ref<Set<GenericField>>(new Set());
  const fieldsSettings: FieldInitialSettings = {
    outcome_date: {
      model: getShelterNow(),
      settings: {
        minDate: getShelterNow(),
        showTime: true,
        withTime: true,
      },
    },
  };

  const rawFields = ref<RawField[]>([
    {
      label: 'Outcome Date',
      name: 'outcome_date',
      required: true,
      type: 'date_with_time_string',
      value: '',
    },
  ]);

  const userCanSetPaidDate = computed(
    () =>
      shelterProfile.value?.can_update_payment_at_cart &&
      shelterProfile.value?.can_update_payment_at_cart.includes(userProfile.value?._id)
  );

  function parseSavedDate(savedDate: string | null | undefined, fallback = getShelterNow()) {
    if (!savedDate) return fallback;

    // NOTE: Parse the saved value directly. Re-formatting it first and handing that to `new Date()`
    // reads it MONTH-first whatever the separator, so a d/m/Y shelter reopening a cart silently got
    // a date two months off rather than an error.
    return getParsedDate(savedDate) ?? fallback;
  }

  function setForm() {
    const newestIntakeDate = getNewestCartAnimalIntakeDate();
    fieldsSettings!.outcome_date!.settings!.minDate = newestIntakeDate ?? getShelterNow();
    fieldsSettings!.outcome_date!.settings!.hint = undefined;

    // Shelter clock, so the fallback below matches the picker's own default. See getShelterNow().
    const now = getShelterNow();
    const fallback = newestIntakeDate && now < newestIntakeDate ? newestIntakeDate : now;
    fieldsSettings!.outcome_date!.model = parseSavedDate(cartData.value?.outcome_date, fallback);

    if (userCanSetPaidDate.value) {
      setPaidDateField();
    }
    fieldsSettings!.outcome_date!.settings!.hint = newestIntakeDate
      ? t('cart.hint.outcomeDateBeforeIntake', {
          intakeDate: getDefaultDateFormat(newestIntakeDate, true),
        })
      : undefined;

    fieldsSchema.value = getDetailsSchema(rawFields.value, fieldsSettings);
  }

  function setPaidDateField() {
    rawFields.value.push({
      label: 'Paid Date',
      name: 'paid_date',
      required: false,
      type: 'date',
      value: null,
    });

    fieldsSettings.paid_date = {
      model: null,
      settings: {
        noModelOnStart: true,
      },
    };
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    rawFields,
    userCanSetPaidDate,
    setForm,
  };
}
